// components/ThemeLoader.jsx
import { useEffect } from 'react';

/**
 * Lista das variantes de tema aceitas. Espelha o whitelist do backend
 * (WhiteLabelConfigService.ValidThemeVariants) — qualquer coisa fora daqui
 * é tratada como "desconhecido" e cai pro default.
 */
const VALID_THEMES = ['blue', 'green', 'orange'];

/**
 * Variante padrão quando nada é configurado. 'green' = padrão da plataforma Mexx.
 * Alinhado com a migration DEFAULT 'green' do banco e o DefaultThemeVariant do C# Service.
 * Hirers sem preferência salva e cenários defensivos (localStorage corrompido, import falhou)
 * caem aqui.
 */
const DEFAULT_THEME = 'green';

/**
 * Resolve qual tema deve ser carregado, com chain defensiva em camadas:
 *
 *   1) localStorage.whitelabel.themevariant (gravado por api.js após auth)
 *      → fonte de verdade quando o user já autenticou.
 *   2) Hostname/env var (compat com comportamento original do ThemeLoader)
 *      → usado pré-auth (tela de login não tem whitelabel ainda).
 *   3) DEFAULT_THEME = 'green'.
 *
 * Toda etapa é envolvida em try/catch — localStorage corrompido, valor não-string,
 * env quebrada, etc., NUNCA propagam exceção. Pior caso o sistema cai no default.
 */
const resolveTheme = () => {
  // (1) whitelabelsession persistido
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('whitelabel') : null;
    if (raw) {
      const ws = JSON.parse(raw);
      const candidate = ws && ws.themevariant;
      if (typeof candidate === 'string') {
        const normalized = candidate.trim().toLowerCase();
        if (VALID_THEMES.includes(normalized)) return normalized;
      }
    }
  } catch (_) {
    // localStorage corrompido, JSON inválido, etc. — segue pro próximo fallback.
  }

  // (2) env var / hostname (comportamento legacy original)
  try {
    const env = (process.env.REACT_APP_THEME || '').toLowerCase();
    if (VALID_THEMES.includes(env)) return env;

    const host = (typeof document !== 'undefined' && document.location && document.location.host) || '';
    if (env === 'green' || host.indexOf('.2do.homologa.') > -1 || host.indexOf('localhost') > -1) {
      return 'green';
    }
  } catch (_) {
    // process.env ou document indisponíveis — segue.
  }

  // (3) safety net
  return DEFAULT_THEME;
};

/**
 * Carrega o CSS correspondente ao tema, com fallback pro CSS default em qualquer falha
 * (build sem o arquivo, network, etc.). Se o default também falhar, silencia — o MUI
 * mantém o tema base que já garante uma renderização funcional.
 */
const loadCssFile = async (theme) => {
  try {
    switch (theme) {
      case 'green':
        await import('../../styles/mexx-variables-redesign.css');
        return;
      case 'orange':
        await import('../../styles/mexx-variables-orange.css');
        return;
      case 'blue':
      default:
        await import('../../styles/mexx-variables.css');
        return;
    }
  } catch (_) {
    // import do tema selecionado falhou (deploy parcial, build sem o arquivo, network).
    // Tentamos o default em silêncio.
    try {
      await import('../../styles/mexx-variables.css');
    } catch (__) {
      // Default também falhou — não tem mais o que fazer; deixa o MUI cuidar.
    }
  }
};

const ThemeLoader = () => {
  useEffect(() => {
    // useEffect já isola erros que escaparem, mas envolvemos em try/catch top-level
    // como cinto-e-suspensório — se algo aqui explodir, o ThemeLoader vira no-op e
    // o sistema segue funcionando com o tema padrão do MUI/CSS atualmente carregado.
    try {
      const theme = resolveTheme();
      loadCssFile(theme);
    } catch (_) {
      // No-op: tema fica como o que estiver carregado, sistema permanece operável.
    }
  }, []);

  return null;
};

export default ThemeLoader;
